<template>
    <div :id="rootElementId" :style="`height:${PITCH_HEIGHT}px`"></div>
</template>

<script>

import { getDataUrl, getNeutralColors, isWhite } from '../../utils/functions';
import { loadFonts,copyAndAppendElement } from '../../utils/exportToPNG';
import ResizeObserver from 'resize-observer-polyfill';
import Dictionary from '../../dictionary';

  export default {
    name: 'XGGoalChart',
    props: {
      id:{
        type: String,
        required: true
      },
      size: {
        type: Number,
        required: false,
      },
      margin: {
        type: Object,
        required: false,
      },
      shots: {
        type: Array,
        required: true,
      },
      game: {
        type: Object,
        required: false
      },
      playerSeasonData: {
        type: Array,
        required: false
      },
      circlesAndBoxes: {
        type: Array,
        required: false
      },
      playerName: {
        type: String,
        required: false
      },
      team:{
        type: Boolean,
        default: false
      },
      playerPosition: {
        type: Number,
        required: false
      },
      language: {
        type: String,
        required: true
      },
      homeFillColor:{
        type: String,
        default: "#ffffff"
      },
      homeBorderColor:{
        type: String,
        default: "#000000"
      },
      awayFillColor:{
        type: String,
        default: "#ffffff"
      },
      awayBorderColor:{
        type: String,
        default: "#000000"
      },
      mediaMode:{
        type: Boolean,
        default: false
      },
      postGameAnalysis:{
        type: Boolean,
        default: false
      },
      xGOnTarget:{
        type: Boolean,
        default: false
      },
      finishingXGOT:{
        type: Boolean,
        default: false
      },
      //gameId for print one game Player xG Map
      gameId:{
        type:Number,
        default: 0
      },
      //gameIds for print number of games selected
      gameIds: {
        type: Array,
        required: false,
        default: () => []
      },
      gameName:{
        type: String,
        default: ""
      },
      fontFamily:{
        type: String,
        default: 'NeueMontreal'
      },
      minsPlayed: {
        type: Number,
        default: 0,
      },
      showPenalties: {
        type: Boolean,
        default: false
      },
    },
    computed: {
      SVG_WIDTH(){
        return this.size ? this.size : 380;
      },
      PITCH_WIDTH(){
        return this.SVG_WIDTH;
      },
      PITCH_HEIGHT() {
        return this.SVG_WIDTH / 1.93;
      },
      rootElementId(){
        return `pitchXG${this.id}`
      },
      currentUserFieldColor() {
        return this.$store.getters["auth/fieldColor"];
      },
      totalPenalties() { 
        return this.shots.filter(d => d.penaltyGoal).length;
      },
      totalPenaltyGoals() {
        return this.shots.filter(d => d.penaltyGoal && d.type === "GOAL").length;
      },
      shotsOutOfRange() {
        return this.shots.filter(d => !(d.y1 < 61 && d.y1 > 39 && d.z_mouth < 92.5));
      }
    },
    data() {
      return {
        height: 0,
        width: 0,
        images: {},
        observer: null,
      }
    },

    mounted() {
      this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));
      this.printD3();
    },
    methods: {
      async printD3(force = false){
        
        if(force){
          this.images = {};
        }

        const SVG_WIDTH = this.SVG_WIDTH;
        const margin = this.margin ? this.margin : {top: 0, right: 0, bottom: 0, left: 0};

        const PITCH_WIDTH = this.PITCH_WIDTH;
        const PITCH_HEIGHT = this.PITCH_HEIGHT;
        //Only shots saved by GK
        let shotsBlockedGK = this.shots.filter(s => !s.blockedByOutfielderPlayer);
        //Filter penalty and ownGoals and shots outside de chart
        let filteredShots = this.showPenalties ? shotsBlockedGK.filter(p => !p.ownGoal) : shotsBlockedGK.filter(p => !p.penaltyGoal).filter(p => !p.ownGoal);
       
        d3.selectAll('.tooltip-no-arrow').remove();

        d3.select(`#${this.rootElementId}`).select("svg").remove();

        const tooltip = d3
          .select("body")
          .append("div")
          .attr("display", "none")
          .attr("class", "tooltip-no-arrow");

        var svg = d3.select(`#${this.rootElementId}`)
          .append("svg")
          .attr("width", SVG_WIDTH)
          .attr("height", PITCH_HEIGHT + margin.top + margin.bottom)
          .style("background-color", "#ffffff")
          .attr("id",`${this.rootElementId}SVG`)
          .attr("class", "pitch");

        this.drawImageBase64(svg, 0, 0, PITCH_WIDTH, PITCH_HEIGHT, require(`@/assets/img/front_goal.png`),"pitch",true);

        // Draw Driblab Logo
        this.drawImageBase64(svg, PITCH_WIDTH - (PITCH_WIDTH * 0.2),
        PITCH_HEIGHT - (PITCH_HEIGHT * 0.18),
        PITCH_WIDTH * 0.18, PITCH_HEIGHT * 0.18,
        require(`@/assets/img/driblab${this.mediaMode ? "_normal" : ""}_white.png`),'logoDriblab');

        let neutralColorsHome = getNeutralColors('home');
        let firstHomeTeamColor = this.homeFillColor;
        let secondHomeTeamColor = this.homeBorderColor;
        let neutralColorsAway = getNeutralColors('away');
        let firstAwayTeamColor =  this.awayFillColor;
        let secondAwayTeamColor = this.awayBorderColor;
        //If is game xG Map but it's post game, don't get team colors, keep neutral colors
        if (this.game && !this.mediaMode && !this.postGameAnalysis) {
          firstHomeTeamColor = this.game.homeTeamGameColor ? this.game.homeTeamGameColor : (this.game.homeTeamColor ? this.game.homeTeamColor : firstHomeTeamColor);
          secondHomeTeamColor = this.game.homeTeamGameSecondColor ? this.game.homeTeamGameSecondColor : (this.game.homeTeamSecondColor ? this.game.homeTeamSecondColor : secondHomeTeamColor);
          firstAwayTeamColor = this.game.awayTeamGameColor ? this.game.awayTeamGameColor : (this.game.awayTeamColor ? this.game.awayTeamColor : firstAwayTeamColor);
          secondAwayTeamColor = this.game.awayTeamGameSecondColor ? this.game.awayTeamGameSecondColor : (this.game.awayTeamSecondColor ? this.game.awayTeamSecondColor : secondAwayTeamColor);
        }
        if(this.game && firstHomeTeamColor === firstAwayTeamColor){
          firstAwayTeamColor = neutralColorsAway[0];
        }
        if(this.game && secondHomeTeamColor === secondAwayTeamColor){
          secondAwayTeamColor = neutralColorsAway[1];
        }

        if (this.circlesAndBoxes && this.circlesAndBoxes.includes("boxes"))
          this.renderBoxes(svg,
            SVG_WIDTH,
            PITCH_HEIGHT,
            margin,
            filteredShots,
            this.language
          )

        if (this.circlesAndBoxes && this.circlesAndBoxes.includes("circles"))
          this.renderShots(svg,
            PITCH_WIDTH,
            PITCH_HEIGHT,
            margin,
            this.rootElementId,
            filteredShots,
            this.xGOnTarget,
            this.playerPosition ? this.playerPosition : "none",
            firstHomeTeamColor.replace("0x", "#"),
            secondHomeTeamColor.replace("0x", "#"),
            firstAwayTeamColor.replace("0x", "#"),
            secondAwayTeamColor.replace("0x", "#"),
            tooltip,
            this.game ? true : false);
       
        this.printPlayerXGLegend(svg, PITCH_WIDTH, margin, filteredShots, this.xGOnTarget, this.finishingXGOT, this.playerSeasonData, this.playerName, this.playerPosition ? this.playerPosition : "none",this.language)

        let font = await loadFonts(this.fontFamily);
        copyAndAppendElement(svg.node(),font.node(),0,0);

        if (this.shotsOutOfRange.length) {
          this.writeText(svg, PITCH_WIDTH * 0.025 , PITCH_HEIGHT * 0.858 + margin.top, "start", (0.0135 * PITCH_WIDTH) + "px", `${ Dictionary[this.language]["finishingMap"]["chart"]["exceptionShotsOutOfRange"] } (${this.shotsOutOfRange.length})`);
        };

        if (this.showPenalties) {
          this.writeText(svg, PITCH_WIDTH * 0.025 , PITCH_HEIGHT * 0.899 + margin.top, "start", (0.0135 * PITCH_WIDTH) + "px", `${this.playerPosition === 1 ? Dictionary[this.language]["finishingMap"]["chart"]["exceptionTextGKPenaltyGoals"] : Dictionary[this.language]["finishingMap"]["chart"]["exceptionTextPenaltyGoals"]}`);
          this.writeText(svg, PITCH_WIDTH * 0.035 , PITCH_HEIGHT * 0.94 + margin.top, "start", (0.0135 * PITCH_WIDTH) + "px", `${this.totalPenaltyGoals}/${this.totalPenalties} ${ Dictionary[this.language]["team"]["overviewTab"]["stats"]["goalsPenalty"] }`);
        };

        //Emit when is loaded
        this.$emit("loaded", this.id);
      },
      measureText(string, fontSize = 10) {
        const widths = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0.278125,0.278125,0.35625,0.55625,
      		0.55625,0.890625,0.6671875,0.1921875,0.334375,0.334375,0.390625,0.584375,0.278125,0.334375,0.278125,0.303125,0.55625,
      		0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.278125,0.278125,0.5859375,0.584375,0.5859375,
      		0.55625,1.015625,0.6671875,0.6671875,0.7234375,0.7234375,0.6671875,0.6109375,0.778125,0.7234375,0.278125,0.5,0.6671875,
      		0.55625,0.834375,0.7234375,0.778125,0.6671875,0.778125,0.7234375,0.6671875,0.6109375,0.7234375,0.6671875,0.9453125,
      		0.6671875,0.6671875,0.6109375,0.278125,0.35625,0.278125,0.478125,0.55625,0.334375,0.55625,0.55625,0.5,0.55625,
      		0.55625,0.278125,0.55625,0.55625,0.2234375,0.2421875,0.5,0.2234375,0.834375,0.55625,0.55625,0.55625,0.55625,
      		0.334375,0.5,0.278125,0.55625,0.5,0.7234375,0.5,0.5,0.5,0.35625,0.2609375,0.3546875,0.590625];
        const avg = 0.5293256578947368;

        return string
          .split('')
          .map(c => c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg)
          .reduce((cur, acc) => acc + cur) * fontSize;
      },
      drawImageBase64(svg,x,y,width,height,img,idName,bottom = false) {
        if(!this.images[idName]){
          let base_image = new Image();
          base_image.crossOrigin = "anonymous";
          base_image.src = img;
          base_image.onload = (event) => {
            const dataUrl = getDataUrl(event.currentTarget);
            this.drawImage(
            svg,
            x,
            y,
            width,
            height,
            dataUrl,
            idName,
            bottom
            );
          }
        }else{
          if(bottom){
            svg.node().insertBefore(this.images[idName],svg.node().firstChild);
          }else{
            svg.node().appendChild(this.images[idName]);
          }
        }
      },
      drawImage: function(svg, x, y, width, height, img,idName,bottom = false) {
        let imgNode = bottom ? svg.insert("svg:image",":first-child") : svg.append("svg:image");
          imgNode.attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .attr("xlink:href", img);
      this.images[idName] = imgNode.node().cloneNode(true);
      },
      drawRectangle(svg, x, y, width, height, color, opacity, className,insertLast = false) {
        let node = insertLast ? svg.insert("rect",":last-child") : svg.append("rect");
        return node
          .attr("class", className)
          .attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .style("fill", color)
          .style("opacity", opacity);
      },
      writeText(svg, x, y, anchor, fontSize, text, fill = "white", className = "none") {
        return svg.append("text")
          .attr("x", x)
          .attr("y", y)
          .attr("text-anchor", anchor)
          .attr("dy", "0.8em")
          .attr("font-weight", 500)
          .attr("class", className)
          .style("font-size", fontSize)
          .style("font-family", this.fontFamily)
          .style("fill", fill)
          .text(text);
      },
      getTypeOfShot(name) {
      	if (name === "SAVED SHOT") return "Saved Shot";
      	if (name === "GOAL") return "Goal";
      	if (name === "MISSED SHOT") return "Missed Shot";
      	if (name === "SHOT ON POST") return "Shot on Post";
        if (name === "MISSED CHANCE") return "Chance Missed";
        if (name === "CHANCE MISSED") return "Chance Missed";
        if (name === "Regular play") return "Regular Play";
        if (name && name.startsWith("Individual")) return "Individual Play";
        if (name === "Fast break") return "Fast Break";
        if (name === "From corner") return "From Corner";
        if (name === "Free kick") return "Free Kick";
        if (name === "Set piece") return "Set Piece";
        if (name && name.startsWith("Throw")) return "Throw In";
        if (name === "Left footed") return "Left Foot";
        if (name === "Right footed") return "Right Foot";
        if (name === "Head") return "Head";
        if (name === "Other body part") return "Other Body Part";
        if (name === "Keeper Throw") return "Keeper Throw";
        if (name === "Penalty") return "Penalty";
        console.log("Unkown " + name);
      	return "??";
      },
      renderShots(svg, pitchSize, pitchHeight, margin, elementId, xgShots, xGOnTargetOption, playerPosition, teamColor, strokeColor, awayTeamColor, awayStrokeColor, tooltip, isGameXG = true) {
        if(this.currentUserFieldColor === 'white'){
          if(isWhite(teamColor)){
            teamColor = "#464646"
          }
          if(isWhite(awayTeamColor)){
            awayTeamColor = "#464646"
          }
        }

        let x = d3.scale.linear().domain([39 , 61]).range([pitchSize, 0]);
        let y = d3.scale.linear().domain([0, 92.5]).range([pitchHeight - (pitchHeight * 0.27), 0]);

        let radius = d3.scale.linear().domain([0, 1]).range([pitchSize * 0.0055, pitchSize * 0.024]);

        var self = this;
        svg.selectAll("circle")
          .data(xgShots)
          .enter()
          .append("circle")
          .attr("r", d => playerPosition === 1 ? radius(d.xG) / 1.5 : radius(d.xG))
          .attr("cx", d => isGameXG ? d.homeTeam ? x(d.x) : x(100 - d.x) : x(d.y1))
          .attr("cy", d => isGameXG ? d.homeTeam ? y(100 - d.y) : y(d.y) : y(d.z_mouth))
          .attr('id', d => "shot" + d.id + "-" + elementId)
          .attr("class", d => "shot-" + d.playerId + " type-" + self.getTypeOfShot(d.type).split(" ").join("-") + " type-" + self.getTypeOfShot(d.strategy).split(" ").join("-") + " type-" + self.getTypeOfShot(d.bodyPart).split(" ").join("-") + " season-" + d.seasonId)
          .style("fill-opacity", (d) => self.getTypeOfShot(d.type) === "Goal" ? 0.90 : 0.25)
          .style("opacity", 0.95)
          .style("stroke", d => "black")
          .style("stroke-width", playerPosition === 1 ? pitchSize * 0.002 / 1.5 : pitchSize * 0.0015)
          .style("fill", d => self.getTypeOfShot(d.type) === "Goal" ? "green" : "gray")
          .on('mouseenter',function(xgShot) {
            d3.select(this)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr('r', playerPosition === 1 ? radius(xgShot.xG) * 1.75 / 1.5 : radius(xgShot.xG) * 1.75);
            var statName = playerPosition === 1 ? 'xGOT' : 'xG';
            var shotId = document.getElementById("shot" + xgShot.id + "-" + elementId);
            var textLine1 = isGameXG ? xgShot.player : xgShot.game;
            var textLine2 = !isGameXG && playerPosition === 1 ? xgShot.player + " · " + xgShot.xG.toFixed(2) + " " + statName : xgShot.xG.toFixed(2) + " " + statName; // For GK we add shooter name to tooltip
            var textLine3 = self.getTypeOfShot(xgShot.type) + " at " + xgShot.min + ":" + (xgShot.sec < 10 ? "0" + xgShot.sec : xgShot.sec);
            var maxTextWidth = Math.max(self.measureText(textLine1, 14), self.measureText(textLine2, 14), self.measureText(textLine3, 14));
            var widthTooltip = maxTextWidth + 60;
            var height = 70;
            var topOffset = 90;
            var leftOffset = Math.ceil(shotId.getAttribute('r'));
            var tooltipTop = shotId.getBoundingClientRect().bottom + 10;
            tooltipTop = isGameXG && ((xgShot.y <= 50 && xgShot.homeTeam) || (!xgShot.homeTeam && xgShot.y >= 50)) ?
            shotId.getBoundingClientRect().top - topOffset + 10 : tooltipTop

            d3.select("body > .tooltip-no-arrow")
            .attr("font-weight", 500)
              .style("width", widthTooltip + "px")
              .style("height", height + "px")
              .style("font-size", "14px")
              .style("left", shotId.getBoundingClientRect().left - (widthTooltip/2) + leftOffset + "px")
              .style("top", (tooltipTop) + "px")
              .style("display", "inline-block")
              .style("position", "fixed")
              .html("<b>" + (isGameXG ? xgShot.player : xgShot.game )
              + "</b></br>" + ((!isGameXG && playerPosition === 1) || self.team ? xgShot.player + " · " : "")
              + "<b>" + xgShot.xG.toFixed(2)
              + " " + statName
              + (xGOnTargetOption && xgShot.xgOnTarget > 0.01 ? ' · ' + xgShot.xgOnTarget.toFixed(2) + ' xG OT' : '')
              + "</b></br>" + self.getTypeOfShot(xgShot.type)
              + " at " + xgShot.min + ":"
              + (xgShot.sec < 10 ? "0" + xgShot.sec : xgShot.sec));
          })
          .on('mouseout',function(xgShot) {
            d3.select(this)
              .transition()
              .duration(200)
              .attr('r', d => playerPosition === 1 ? radius(d.xG) / 1.5 : radius(d.xG));
            d3.select("body > .tooltip-no-arrow").style("display", "none");
          })
          .transition()
          .duration(1000)
      },
      printPlayerXGLegend(svg, pitchWidth, margin, shots, xGOnTargetOption, finishingXGOTOption, seasons, playerName,playerPosition, language = "en") {

        //Remove Penalty and own goals to count goals and xG
        let availableShots = this.showPenalties ? shots.filter(p => !p.ownGoal || this.team) : shots.filter(p => !p.penaltyGoal).filter(p => !p.ownGoal || this.team);
        const xG = availableShots.map(shot => shot.xG).reduce((a, b) => a + b, 0);
        const xGOnTarget = availableShots.map(shot => shot.xgOnTarget).reduce((a, b) => a + b, 0);
        const finishingXGOT = availableShots.map(shot => shot.finishingXGOT).reduce((a, b) => a + b, 0);
        const shotsOnTarget = availableShots.filter(shot => shot.type === "GOAL").length + availableShots.filter(shot => shot.type === "SAVED SHOT" && !shot.blockedByOutfielderPlayer).length;
        const playerShots = availableShots.length;
        const goals = availableShots.length === 0 ? 0 : availableShots.filter(d => d.type === "GOAL").length;
        //If games selected, then get number of games received, if not, take gamesPlayed of seasons
        const games = this.gameIds.length ? this.gameIds.length : seasons.map(s => s.gamesPlayed).reduce((a, b) => a + b, 0);
        const minutes = this.minsPlayed > 0 ? this.minsPlayed : seasons.map(s => s.minutesPlayed).reduce((a, b) => a + b, 0);
        const rectangleTopSize = this.PITCH_WIDTH * 0.98;
        const rectangleTopY = this.PITCH_HEIGHT * 0.02;
        let textColor = 'white';
        let xGDecimals = 1;

        this.drawRectangle(svg,
          this.PITCH_WIDTH * 0.01, // x
          rectangleTopY, // y
          rectangleTopSize, // width
          (0.022 * pitchWidth) + 8, // height
          "black",
          0.85,
          "xgPlayerTopLegendRect");

        let legendLeftX = pitchWidth * 0.015 + margin.left;
        let legendLeftY = this.PITCH_HEIGHT * 0.035;
        if(this.gameId == 0){
          let headerText = `${ Dictionary[this.language]["table"]["history"]["games"] }: ${ games }`;
          if(!this.team){
            //Just show minutes for Player GoalChart
            headerText += ` · ${ Dictionary[this.language]["table"]["roster"]["minutes"] }: ${ minutes }`;
          }
          this.writeText(svg,
            legendLeftX,
            legendLeftY,
            "start",
            (0.022 * pitchWidth) + "px",
            headerText,
            textColor, "playerGamesAndMinutes");
        }


        let legendRightX = pitchWidth * 0.985 + margin.left;
        let legendRightY = this.PITCH_HEIGHT * 0.035;
        let legendRightEl = this.writeText(svg, pitchWidth * 0.985 + margin.left, this.PITCH_HEIGHT * 0.035, "end", (0.022 * pitchWidth) + "px", `${ playerShots } ${ this.$t("game.stats.shots")} · ${ playerShots > 0 ? (xG / playerShots).toFixed(2) : 0 } xG  ${ this.$t("finishingMap.perShot")}`, textColor, "playerShotsAndXG");

        const numberOfSeasons = seasons.length;

        const rectangleSize = numberOfSeasons === 2 ? this.PITCH_HEIGHT * 0.245 : this.PITCH_HEIGHT * 0.22;
        const rectangleY = numberOfSeasons === 2 ? this.PITCH_HEIGHT * 0.745 : this.PITCH_HEIGHT * 0.76;
        this.drawRectangle(svg,
          (pitchWidth / 2 - 0.45 * pitchWidth / 2) + margin.left, // x
          rectangleY, // y
          0.45 * pitchWidth, // width
          rectangleSize, // height
          "black",
          0.85,
          "xgPlayerLegendRect");

        this.writeText(svg,
          pitchWidth * 0.505 + margin.left,
          rectangleY + 5,
          "middle",
          (0.04 * this.PITCH_HEIGHT) + "px",
          playerName,
          "white",
          "playerXGName");

        svg.selectAll(".leagueAlias")
          .transition()
          .duration(200)
          .style("font-size", "0px");
        if(this.gameId == 0 && this.gameName == ""){
          if(numberOfSeasons < 3){
            svg.selectAll(".leagueAlias")
              .data(seasons)
              .enter()
              .append("text")
              .attr("class", "leagueAlias")
              .attr("x", (pitchWidth * 0.505) + margin.left)
              .attr("y", (d, i) =>  rectangleY + (rectangleSize / 2) + (i === 0 ? -5 : (0.0325 * this.PITCH_HEIGHT * i)) - ((finishingXGOTOption ? 0.04 : 0.0325) * this.PITCH_HEIGHT))
              .attr("text-anchor", "middle")
              .attr("dy", "0.8em")
              .attr("font-weight", 500)
              .style("font-size", (0.0325 * this.PITCH_HEIGHT) + "px")
              .style("font-family", this.fontFamily)
              .style("fill", "white")
              .text(d => d.seasonName);
          }else{
            svg
              .append("text")
              .attr("class", "leagueAlias")
              .attr("x", (pitchWidth * 0.505) + margin.left)
              .attr("y", rectangleY + (rectangleSize / 2) - ((finishingXGOTOption ? 0.04 : 0.0325) * this.PITCH_HEIGHT))
              .attr("text-anchor", "middle")
              .attr("dy", "0.8em")
              .attr("font-weight", 500)
              .style("font-size", (0.0325 * this.PITCH_HEIGHT) + "px")
              .style("font-family", this.fontFamily)
              .style("fill", "white")
              .text(d => "Several Seasons");
          }
        }else{
          xGDecimals = 2;
          //Print game name
          this.writeText(svg,
            pitchWidth * 0.505 + margin.left,//pitchWidth / 2 + margin.left,
            rectangleY + 27, //0.65 * pitchWidth + margin.top,
            "middle",
            (0.019 * pitchWidth) + "px",
            this.gameName,
            "white",
            "gameName"
          );
        }


        let statName = playerPosition === 1 ? 'xGOT' : 'xG';
        this.writeText(svg,
          pitchWidth * 0.505 + margin.left,
          (this.PITCH_HEIGHT * (finishingXGOTOption ? 0.7 : 0.73)) + (rectangleSize * 0.8),
          "middle",
          (0.023 * pitchWidth) + "px",
          `${ goals } ${this.$t("table.roster.goals")} ${this.showPenalties && this.totalPenaltyGoals > 0 ? `(${this.totalPenaltyGoals} P)` : ''} · ${ xG.toFixed(xGDecimals) } ${statName} ${xGOnTargetOption ? '· ' + xGOnTarget.toFixed(xGDecimals) + ' xG OT' : '' }`,
          "white",
          "playerXGandGoals");

        if (finishingXGOTOption && shotsOnTarget > 0) {
          this.writeText(svg,
            pitchWidth * 0.505 + margin.left,
            rectangleY + rectangleSize - (0.048 * this.PITCH_HEIGHT),
            "middle",
            (0.023 * pitchWidth) + "px",
            `${ (finishingXGOT / shotsOnTarget) > 1 ? '+': ''}${ (((finishingXGOT / shotsOnTarget) - 1) * 100 ).toFixed(xGDecimals) }% Finishing xG OT`,
            "white",
            "playerXGandGoals"
          );
        }

      },
      renderBoxes(svg, svgWidth, pitchHeight, margin, shots, language = 'en') {
        let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';

        let x = d3.scale.linear().domain([39 , 61]).range([this.PITCH_WIDTH, 0]);
        let y = d3.scale.linear().domain([0, 92.5]).range([this.PITCH_HEIGHT - (this.PITCH_HEIGHT * 0.27), 0]);


        const shotLegend = this.$t("statsNaming.Shots").toLowerCase();

        const goalAreaLowRight = shots.filter(s => s.z_mouth <= 20 && s.y1 >= 45.2 && s.y1 <= 48.2).length;
        const goalAreaLowCenter = shots.filter(s => s.z_mouth <= 20 && s.y1 >= 48.2 && s.y1 <= 51.8).length;
        const goalAreaLowLeft = shots.filter(s => s.z_mouth <= 20 && s.y1 >= 51.8 && s.y1 <= 54.8).length;

        const goalAreaHighRight = shots.filter(s => s.z_mouth >= 20 && s.z_mouth <= 38 && s.y1 >= 45.2 && s.y1 <= 48.2).length;
        const goalAreaHighCenter = shots.filter(s => s.z_mouth >= 20 && s.z_mouth <= 38 && s.y1 >= 48.2 && s.y1 <= 51.8).length;
        const goalAreaHighLeft = shots.filter(s => s.z_mouth >= 20 && s.z_mouth <= 38 && s.y1 >= 51.8 && s.y1 <= 54.8).length;

        const closeAreaLeft = shots.filter(s => s.z_mouth <= 40 && s.y1 >= 55.8 && s.y1 <= 61).length;
        const closeAreaHighLeft = shots.filter(s => s.z_mouth >= 40 && s.z_mouth <= 60 && s.y1 >= 55.8 && s.y1 <= 61).length;
        const areaHighLeft = shots.filter(s => s.z_mouth >= 60 && s.z_mouth <= 92.5 && s.y1 >= 55.8 && s.y1 <= 61).length;

        const closeAreaRight = shots.filter(s => s.z_mouth <= 40 && s.y1 >= 39 && s.y1 <= 44.7).length;
        const closeAreaHighRight = shots.filter(s => s.z_mouth >= 40 && s.z_mouth <= 60 && s.y1 >= 39 && s.y1 <= 44.7).length;
        const areaHighRight = shots.filter(s => s.z_mouth >= 60 && s.z_mouth <= 92.5 && s.y1 >= 39 && s.y1 <= 44.7).length;

        const closeHighCenter = shots.filter(s => s.z_mouth >= 40 && s.z_mouth <= 60 && s.y1 >= 44.2 && s.y1 <= 55.8).length;
        const highCenter = shots.filter(s => s.z_mouth >= 60 && s.z_mouth <= 92.5 && s.y1 >= 44.2 && s.y1 <= 55.8).length;

        var shadowScale = d3.scale.linear().domain([0, shots.length]).range([0.1, 0.75]);

        if (goalAreaLowRight > 0.5) {
          let goalAreaLowRightX = x(48.2);
          let goalAreaLowRightY = y(20);
          let goalAreaLowRightWidth = x(44.7) - goalAreaLowRightX;
          let goalAreaLowRightHeight = y(0) - goalAreaLowRightY;
          this.drawRectangle(svg, goalAreaLowRightX, goalAreaLowRightY, goalAreaLowRightWidth, goalAreaLowRightHeight, "red", shadowScale(goalAreaLowRight), "gameLegend1");
          this.writeText(svg, goalAreaLowRightX + (goalAreaLowRightWidth / 2),goalAreaLowRightY + (goalAreaLowRightHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(goalAreaLowRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, goalAreaLowRightX + (goalAreaLowRightWidth / 2),goalAreaLowRightY + (goalAreaLowRightHeight / 2) + 7, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(goalAreaLowRight) } ${ shotLegend })`,textColor);
        }
        if (goalAreaLowCenter > 0.5) {
          let goalAreaLowCenterX = x(51.7);
          let goalAreaLowCenterY = y(20);
          let goalAreaLowCenterWidth = x(48.3) - goalAreaLowCenterX;
          let goalAreaLowCenterHeight = y(0) - goalAreaLowCenterY;
          this.drawRectangle(svg, goalAreaLowCenterX, goalAreaLowCenterY, goalAreaLowCenterWidth, goalAreaLowCenterHeight, "red", shadowScale(goalAreaLowCenter), "gameLegend1");
          this.writeText(svg, goalAreaLowCenterX + (goalAreaLowCenterWidth / 2),goalAreaLowCenterY + (goalAreaLowCenterHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(goalAreaLowCenter / shots.length * 100) } %`,textColor);
          this.writeText(svg, goalAreaLowCenterX + (goalAreaLowCenterWidth / 2),goalAreaLowCenterY + (goalAreaLowCenterHeight / 2) + 7, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(goalAreaLowCenter) } ${ shotLegend })`,textColor);
        }
        if (goalAreaLowLeft > 0.5) {
          let goalAreaLowLeftX = x(55.25);
          let goalAreaLowLeftY = y(20);
          let goalAreaLowLeftWidth = x(51.8) - goalAreaLowLeftX;
          let goalAreaLowLeftHeight = y(0) - goalAreaLowLeftY;
          this.drawRectangle(svg, goalAreaLowLeftX, goalAreaLowLeftY, goalAreaLowLeftWidth, goalAreaLowLeftHeight, "red", shadowScale(goalAreaLowLeft), "gameLegend1");
          this.writeText(svg, goalAreaLowLeftX + (goalAreaLowLeftWidth / 2),goalAreaLowLeftY + (goalAreaLowLeftHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(goalAreaLowLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, goalAreaLowLeftX + (goalAreaLowLeftWidth / 2),goalAreaLowLeftY + (goalAreaLowLeftHeight / 2) + 7, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(goalAreaLowLeft) } ${ shotLegend })`,textColor);
        }

        if (goalAreaHighRight > 0.5) {
          let goalAreaHighRightX = x(48.2);
          let goalAreaHighRightY = y(40);
          let goalAreaHighRightWidth = x(44.7) - goalAreaHighRightX;
          let goalAreaHighRightHeight = y(21) - goalAreaHighRightY;
          this.drawRectangle(svg, goalAreaHighRightX, goalAreaHighRightY, goalAreaHighRightWidth, goalAreaHighRightHeight, "red", shadowScale(goalAreaHighRight), "gameLegend1");
          this.writeText(svg, goalAreaHighRightX + (goalAreaHighRightWidth / 2),goalAreaHighRightY + (goalAreaHighRightHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(goalAreaHighRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, goalAreaHighRightX + (goalAreaHighRightWidth / 2),goalAreaHighRightY + (goalAreaHighRightHeight / 2) + 6, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(goalAreaHighRight) } ${ shotLegend })`,textColor);
        }
        if (goalAreaHighCenter > 0.5) {
          let goalAreaHighCenterX = x(51.7);
          let goalAreaHighCenterY = y(40);
          let goalAreaHighCenterWidth = x(48.3) - goalAreaHighCenterX;
          let goalAreaHighCenterHeight = y(21) - goalAreaHighCenterY;
          this.drawRectangle(svg, goalAreaHighCenterX, goalAreaHighCenterY, goalAreaHighCenterWidth, goalAreaHighCenterHeight, "red", shadowScale(goalAreaHighCenter), "gameLegend1");
          this.writeText(svg, goalAreaHighCenterX + (goalAreaHighCenterWidth / 2),goalAreaHighCenterY + (goalAreaHighCenterHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(goalAreaHighCenter / shots.length * 100) } %`,textColor);
          this.writeText(svg, goalAreaHighCenterX + (goalAreaHighCenterWidth / 2),goalAreaHighCenterY + (goalAreaHighCenterHeight / 2) + 6, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(goalAreaHighCenter) } ${ shotLegend })`,textColor);
        }
        if (goalAreaHighLeft > 0.5) {
          let goalAreaHighLeftX = x(55.25);
          let goalAreaHighLeftY = y(40);
          let goalAreaHighLeftWidth = x(51.8) - goalAreaHighLeftX;
          let goalAreaHighLeftHeight = y(21) - goalAreaHighLeftY;
          this.drawRectangle(svg, goalAreaHighLeftX, goalAreaHighLeftY, goalAreaHighLeftWidth, goalAreaHighLeftHeight, "red", shadowScale(goalAreaHighLeft), "gameLegend1");
          this.writeText(svg, goalAreaHighLeftX + (goalAreaHighLeftWidth / 2),goalAreaHighLeftY + (goalAreaHighLeftHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(goalAreaHighLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, goalAreaHighLeftX + (goalAreaHighLeftWidth / 2),goalAreaHighLeftY + (goalAreaHighLeftHeight / 2) + 6, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(goalAreaHighLeft) } ${ shotLegend })`,textColor);
        }

        if (closeAreaLeft > 0.5) {
          let closeAreaLeftX = x(60.9);
          let closeAreaLeftY = y(40);
          let closeAreaLeftWidth = x(55.4) - closeAreaLeftX;
          let closeAreaLeftHeight = y(0) - closeAreaLeftY;
          this.drawRectangle(svg, closeAreaLeftX, closeAreaLeftY, closeAreaLeftWidth, closeAreaLeftHeight, "red", shadowScale(closeAreaLeft), "gameLegend1");
          this.writeText(svg, closeAreaLeftX + (closeAreaLeftWidth / 2),closeAreaLeftY + (closeAreaLeftHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(closeAreaLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, closeAreaLeftX + (closeAreaLeftWidth / 2),closeAreaLeftY + (closeAreaLeftHeight / 2) + 6, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(closeAreaLeft) } ${ shotLegend })`,textColor);
        }
        if (closeAreaHighLeft > 0.5) {
          let closeAreaHighLeftX = x(60.9);
          let closeAreaHighLeftY = y(60);
          let closeAreaHighLeftWidth = x(55.4) - closeAreaHighLeftX;
          let closeAreaHighLeftHeight = y(41) - closeAreaHighLeftY;
          this.drawRectangle(svg, closeAreaHighLeftX, closeAreaHighLeftY, closeAreaHighLeftWidth, closeAreaHighLeftHeight, "red", shadowScale(closeAreaHighLeft), "gameLegend1");
          this.writeText(svg, closeAreaHighLeftX + (closeAreaHighLeftWidth / 2),closeAreaHighLeftY + (closeAreaHighLeftHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(closeAreaHighLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, closeAreaHighLeftX + (closeAreaHighLeftWidth / 2),closeAreaHighLeftY + (closeAreaHighLeftHeight / 2) + 2, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(closeAreaHighLeft) } ${ shotLegend })`,textColor);
        }
        if (areaHighLeft > 0.5) {
          let areaHighLeftX = x(60.9);
          let areaHighLeftY = y(91.75);
          let areaHighLeftWidth = x(55.4) - areaHighLeftX;
          let areaHighLeftHeight = y(61) - areaHighLeftY;
          this.drawRectangle(svg, areaHighLeftX, areaHighLeftY, areaHighLeftWidth, areaHighLeftHeight, "red", shadowScale(areaHighLeft), "gameLegend1");
          this.writeText(svg, areaHighLeftX + (areaHighLeftWidth / 2),areaHighLeftY + (areaHighLeftHeight / 2) - 7, "middle", (0.018 * svgWidth) + "px", `${ Math.round(areaHighLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, areaHighLeftX + (areaHighLeftWidth / 2),areaHighLeftY + (areaHighLeftHeight / 2) + 6, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(areaHighLeft) } ${ shotLegend })`,textColor);
        }

        if (closeAreaRight > 0.5) {
          let closeAreaRightX = x(44.55);
          let closeAreaRightY = y(40);
          let closeAreaRightWidth = x(39.1) - closeAreaRightX;
          let closeAreaRightHeight = y(0) - closeAreaRightY;
          this.drawRectangle(svg, closeAreaRightX, closeAreaRightY, closeAreaRightWidth, closeAreaRightHeight, "red", shadowScale(closeAreaRight), "gameLegend1");
          this.writeText(svg, closeAreaRightX + (closeAreaRightWidth / 2),closeAreaRightY + (closeAreaRightHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(closeAreaRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, closeAreaRightX + (closeAreaRightWidth / 2),closeAreaRightY + (closeAreaRightHeight / 2) + 6, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(closeAreaRight) } ${ shotLegend })`,textColor);
        }
        if (closeAreaHighRight > 0.5) {
          let closeAreaHighRightX = x(44.55);
          let closeAreaHighRightY = y(60);
          let closeAreaHighRightWidth = x(39.1) - closeAreaHighRightX;
          let closeAreaHighRightHeight = y(41) - closeAreaHighRightY;
          this.drawRectangle(svg, closeAreaHighRightX, closeAreaHighRightY, closeAreaHighRightWidth, closeAreaHighRightHeight, "red", shadowScale(closeAreaHighRight), "gameLegend1");
          this.writeText(svg, closeAreaHighRightX + (closeAreaHighRightWidth / 2),closeAreaHighRightY + (closeAreaHighRightHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(closeAreaHighRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, closeAreaHighRightX + (closeAreaHighRightWidth / 2),closeAreaHighRightY + (closeAreaHighRightHeight / 2) + 2, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(closeAreaHighRight) } ${ shotLegend })`,textColor);
        }
        if (areaHighRight > 0.5) {
          let areaHighRightX = x(44.55);
          let areaHighRightY = y(91.75);
          let areaHighRightWidth = x(39.1) - areaHighRightX;
          let areaHighRightHeight = y(61) - areaHighRightY;
          this.drawRectangle(svg, areaHighRightX, areaHighRightY, areaHighRightWidth, areaHighRightHeight, "red", shadowScale(areaHighRight), "gameLegend1");
          this.writeText(svg, areaHighRightX + (areaHighRightWidth / 2),areaHighRightY + (areaHighRightHeight / 2) - 7, "middle", (0.018 * svgWidth) + "px", `${ Math.round(areaHighRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, areaHighRightX + (areaHighRightWidth / 2),areaHighRightY + (areaHighRightHeight / 2) + 6, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(areaHighRight) } ${ shotLegend })`,textColor);
        }

        if (closeHighCenter > 0.5) {
          let closeHighCenterX = x(55.3);
          let closeHighCenterY = y(60);
          let closeHighCenterWidth = x(44.65) - closeHighCenterX;
          let closeHighCenterHeight = y(41.5) - closeHighCenterY;
          this.drawRectangle(svg, closeHighCenterX, closeHighCenterY, closeHighCenterWidth, closeHighCenterHeight, "red", shadowScale(closeHighCenter), "gameLegend1");
          this.writeText(svg, closeHighCenterX + (closeHighCenterWidth / 2),closeHighCenterY + (closeHighCenterHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(closeHighCenter / shots.length * 100) } %`,textColor);
          this.writeText(svg, closeHighCenterX + (closeHighCenterWidth / 2),closeHighCenterY + (closeHighCenterHeight / 2) + 2, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(closeHighCenter) } ${ shotLegend })`,textColor);
        }
        if (highCenter > 0.5) {
          let highCenterX = x(55.3);
          let highCenterY = y(91.75);
          let highCenterWidth = x(44.65) - highCenterX;
          let highCenterHeight = y(61) - highCenterY;
          this.drawRectangle(svg, highCenterX, highCenterY, highCenterWidth, highCenterHeight, "red", shadowScale(highCenter), "gameLegend1");
          this.writeText(svg, highCenterX + (highCenterWidth / 2),highCenterY + (highCenterHeight / 2) - 7, "middle", (0.018 * svgWidth) + "px", `${ Math.round(highCenter / shots.length * 100) } %`,textColor);
          this.writeText(svg, highCenterX + (highCenterWidth / 2),highCenterY + (highCenterHeight / 2) + 6, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(highCenter) } ${ shotLegend })`,textColor);
        }
      },
      onResize(){
        let elto =document.getElementById(this.rootElementId);
        if(elto){
          this.$emit("sizeChange", {height:elto.clientHeight,width:elto.clientWidth});
        }
      }
    },
    destroyed() {
      delete this.observer;
      d3.select(`#${this.rootElementId}`).select("svg").remove();
    },
  }

</script>



// WEBPACK FOOTER //
// src/views/base/XGGoalChart.vue